<template>
  <modal
    name="poll-vote-modal"
    :click-to-close="true"
    :width="560"
    height="auto"
    class="modal-custom poll-vote-modal-wrap"
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <vue-element-loading :active="loading" spinner="bar-fade-scale" />
    <div class="poll-vote-modal" v-if="poll">
      <div class="poll-vote-modal__header">
        <div class="poll-vote-modal__title">Bình chọn</div>
        <button type="button" class="poll-vote-modal__close" @click="$modal.hide('poll-vote-modal')">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="poll-vote-modal__body custom-scroll-bar-2">
        <div class="poll-vote-modal__question">{{ poll.question }}</div>
        <div class="poll-vote-modal__meta" v-if="creatorLabel">{{ creatorLabel }}</div>

        <div class="poll-vote-modal__settings">
          <div class="poll-vote-modal__settings-item" v-if="!pollSingleChoice">
            <span class="material-icons poll-vote-modal__settings-icon">check_box</span>
            Cho phép chọn nhiều câu trả lời
          </div>
          <div class="poll-vote-modal__settings-item" v-if="pollIsAddOption">
            <span class="material-icons poll-vote-modal__settings-icon">add_circle_outline</span>
            Cho phép chọn thêm trả lời
          </div>
          <div
            class="poll-vote-modal__settings-item"
            v-if="pollExpiredTime"
            :class="{ 'poll-vote-modal__settings-item--expired': isExpired }"
          >
            <span class="material-icons poll-vote-modal__settings-icon">schedule</span>
            {{ expiryLabel }}
          </div>
        </div>

        <div class="poll-vote-modal__expired-banner" v-if="isExpired">
          Cuộc bình chọn đã hết hạn
        </div>

        <button type="button" class="poll-vote-modal__stats" v-if="uniqueVoterCount > 0" @click="openVoteDetail()">
          {{ uniqueVoterCount }} người bình chọn, {{ totalVoteCount }} lượt bình chọn
          <span class="material-icons">chevron_right</span>
        </button>

        <div class="poll-vote-modal__options">
          <div
            v-for="(option, index) in localOptions"
            :key="'opt-' + index"
            class="poll-vote-modal__option-row"
            @click="toggleOption(index)"
          >
            <span class="poll-vote-modal__selector" :class="selectorClass(option)"></span>
            <div class="poll-vote-modal__option-bar">
              <div
                class="poll-vote-modal__option-fill"
                :style="{ width: optionPercent(option) + '%' }"
              ></div>
              <div class="poll-vote-modal__option-content">
                <span class="poll-vote-modal__option-text">{{ option.answer }}</span>
                <div
                  class="poll-vote-modal__option-avatars"
                  v-if="optionAvatarUsers(option).length"
                  @click.stop="openVoteDetail(index)"
                >
                  <image-template
                    v-for="(u, ui) in optionAvatarUsers(option).slice(0, 3)"
                    :key="'av-' + ui"
                    :thumbnail="u.thumbnail"
                    :image="u.avatar"
                    :name="u.display_name || u.fullname || ''"
                    size="small"
                    width="24px"
                    height="24px"
                    class="poll-vote-modal__avatar"
                  />
                  <span v-if="option.vote_ids.length > 3" class="poll-vote-modal__avatar-more">
                    +{{ option.vote_ids.length - 3 }}
                  </span>
                </div>
              </div>
            </div>
            <span
              class="poll-vote-modal__option-count"
              :class="{ 'poll-vote-modal__option-count--clickable': option.vote_ids.length > 0 }"
              @click.stop="option.vote_ids.length > 0 && openVoteDetail(index)"
            >{{ option.vote_ids.length }}</span>
          </div>
        </div>

        <button
          type="button"
          class="poll-vote-modal__add-option"
          v-if="pollIsAddOption && !isExpired"
          @click="showAddOption = !showAddOption"
        >
          <span class="material-icons">add</span>
          Thêm lựa chọn
        </button>
        <div class="poll-vote-modal__add-input" v-if="showAddOption && pollIsAddOption && !isExpired">
          <input
            v-model="answerNew"
            class="poll-vote-modal__input"
            placeholder="Nhập lựa chọn mới..."
            @keyup.enter="addOption"
          />
          <button type="button" class="poll-vote-modal__add-btn" @click="addOption">Thêm</button>
        </div>
      </div>

      <div class="poll-vote-modal__footer">
        <div class="poll-vote-modal__footer-actions poll-vote-modal__footer-actions--full">
          <button type="button" class="poll-vote-modal__btn poll-vote-modal__btn--ghost" @click="$modal.hide('poll-vote-modal')">
            Hủy
          </button>
          <button
            type="button"
            class="poll-vote-modal__btn poll-vote-modal__btn--primary"
            :disabled="isExpired || submitting || pendingAnswers.length === 0"
            @click="confirmVote"
          >
            Xác nhận
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import moment from 'moment';
import ChatService from '@/store/services/ChatService';
import ImageTemplate from '@/components/ImageTemplate.vue';

export default {
  name: 'PollVoteModal',
  components: {
    ImageTemplate,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      poll: null,
      sourceMess: null,
      localOptions: [],
      pendingAnswers: [],
      answerNew: '',
      showAddOption: false,
    };
  },
  computed: {
    currentUserId() {
      return this.$store.state.auth.user.id;
    },
    pollSingleChoice() {
      return !!this.poll?.single_choice;
    },
    pollIsAddOption() {
      return !!this.poll?.is_add_option;
    },
    pollExpiredTime() {
      return this.poll?.expired_time || null;
    },
    isExpired() {
      if (!this.pollExpiredTime) return false;
      const expired = moment(this.pollExpiredTime, ['YYYY-MM-DD HH:mm:ss', 'yyyy-MM-DD HH:mm:ss'], true);
      return expired.isValid() && expired.isBefore(moment());
    },
    expiryLabel() {
      if (!this.pollExpiredTime) return '';
      const expired = moment(this.pollExpiredTime, ['YYYY-MM-DD HH:mm:ss', 'yyyy-MM-DD HH:mm:ss'], true);
      if (!expired.isValid()) return 'Giới hạn thời gian cho cuộc bình chọn';
      if (this.isExpired) {
        return `Đã hết hạn lúc ${expired.format('HH:mm, DD/MM/YYYY')}`;
      }
      return `Kết thúc lúc ${expired.format('HH:mm, DD/MM/YYYY')}`;
    },
    totalVoteCount() {
      return this.localOptions.reduce((sum, item) => sum + (item.vote_ids?.length || 0), 0);
    },
    uniqueVoterCount() {
      const ids = new Set();
      this.localOptions.forEach((item) => {
        (item.vote_ids || []).forEach((id) => ids.add(id));
      });
      return ids.size;
    },
    creatorLabel() {
      if (!this.sourceMess?.sender) return '';
      const name = this.sourceMess.sender.display_name || this.sourceMess.sender.name || '';
      const when = this.sourceMess.created_at
        ? moment(this.sourceMess.created_at).fromNow()
        : '';
      return name ? `Tạo bởi ${name}${when ? ` · ${when}` : ''}` : '';
    },
  },
  methods: {
    beforeOpen(event) {
      this.sourceMess = event.params?.mess || null;
      const pollFromMess = this.sourceMess?.poll;
      if (!pollFromMess?.id) {
        this.poll = null;
        return;
      }
      this.poll = this.normalizePoll(pollFromMess);
      this.syncFromPoll(this.poll);
      this.fetchPollDetail(pollFromMess.id);
    },
    beforeClose() {
      this.poll = null;
      this.sourceMess = null;
      this.localOptions = [];
      this.pendingAnswers = [];
      this.answerNew = '';
      this.showAddOption = false;
      this.submitting = false;
    },
    normalizePoll(poll) {
      if (!poll) return null;
      return {
        ...poll,
        single_choice: !!poll.single_choice,
        is_add_option: !!poll.is_add_option,
        data: this.normalizePollOptions(poll.data),
      };
    },
    normalizePollOptions(data) {
      const list = Array.isArray(data) ? data : Object.values(data || {});
      return list.map((item) => ({ ...item }));
    },
    syncFromPoll(poll) {
      const selected = [];
      this.localOptions = this.normalizePollOptions(poll?.data).map((item) => {
        const row = { ...item };
        if ((row.vote_ids || []).includes(this.currentUserId)) {
          selected.push(row.answer);
        }
        return row;
      });
      this.pendingAnswers = [...selected];
    },
    optionAvatarUsers(option) {
      if (option?.users?.length) {
        return option.users;
      }
      return (option?.vote_ids || []).map((id) => ({ id }));
    },
    async fetchPollDetail(pollId) {
      this.loading = true;
      try {
        const res = await ChatService.getPollDetail(pollId);
        if (res?.status && res.data) {
          this.poll = this.normalizePoll({
            ...this.poll,
            ...res.data,
            data: res.data.data || this.poll.data,
          });
          this.syncFromPoll(this.poll);
        }
      } finally {
        this.loading = false;
      }
    },
    selectorClass(option) {
      const checked = this.pendingAnswers.includes(option.answer);
      return {
        'poll-vote-modal__selector--checked': checked,
        'poll-vote-modal__selector--radio': this.pollSingleChoice,
        'poll-vote-modal__selector--checkbox': !this.pollSingleChoice,
      };
    },
    optionPercent(option) {
      if (!this.totalVoteCount) return 0;
      return Math.round(((option.vote_ids?.length || 0) / this.totalVoteCount) * 100);
    },
    toggleOption(index) {
      if (this.isExpired) return;
      const answer = this.localOptions[index].answer;
      if (this.pollSingleChoice) {
        this.pendingAnswers = this.pendingAnswers.includes(answer) ? [] : [answer];
        return;
      }
      if (this.pendingAnswers.includes(answer)) {
        this.pendingAnswers = this.pendingAnswers.filter((item) => item !== answer);
      } else {
        this.pendingAnswers = [...this.pendingAnswers, answer];
      }
    },
    async confirmVote() {
      if (!this.poll?.id || this.submitting || this.isExpired) return;
      this.submitting = true;
      try {
        const res = await ChatService.votePollBatch(this.poll.id, {
          answers: this.pendingAnswers,
          fcm_token: localStorage.getItem('cmf_token'),
        });
        if (res?.status) {
          this.$modal.hide('poll-vote-modal');
        } else {
          this.$store.dispatch('alerts/error', res?.message || 'Không thể bình chọn');
        }
      } catch (e) {
        this.$store.dispatch('alerts/error', 'Không thể bình chọn');
      } finally {
        this.submitting = false;
      }
    },
    async addOption() {
      const value = this.answerNew.trim();
      if (!value || !this.poll?.id) return;
      const res = await ChatService.addOptionPoll(this.poll.id, {
        answer: value,
        fcm_token: localStorage.getItem('cmf_token'),
      });
      if (res?.status) {
        this.poll = this.normalizePoll({
          ...this.poll,
          data: res.data,
        });
        this.syncFromPoll(this.poll);
        this.answerNew = '';
        this.showAddOption = false;
      } else {
        this.$store.dispatch('alerts/error', res?.message || 'Không thể thêm lựa chọn');
      }
    },
    openVoteDetail(focusOptionIndex = null) {
      if (!this.poll?.id || this.uniqueVoterCount === 0) return;
      const params = {
        pollId: this.poll.id,
        localOptions: this.localOptions,
        sourceMess: this.sourceMess,
        focusOptionIndex,
      };
      this.$modal.hide('poll-vote-modal');
      this.$nextTick(() => {
        this.$modal.show('poll-vote-detail-modal', params);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.poll-vote-modal {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}

.poll-vote-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e9eaec;
}

.poll-vote-modal__title {
  font-size: 18px;
  font-weight: 600;
  color: #26282c;
}

.poll-vote-modal__close {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #65686c;
}

.poll-vote-modal__body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 16px 20px 8px;
}

.poll-vote-modal__question {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  color: #1c2b4a;
}

.poll-vote-modal__meta {
  margin-top: 8px;
  font-size: 13px;
  color: #8a8d91;
}

.poll-vote-modal__settings {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}

.poll-vote-modal__settings-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #65686c;
  line-height: 18px;
}

.poll-vote-modal__settings-item--expired {
  color: #d93025;
}

.poll-vote-modal__settings-icon {
  font-size: 16px;
  color: #30a960;
}

.poll-vote-modal__settings-item--expired .poll-vote-modal__settings-icon {
  color: #d93025;
}

.poll-vote-modal__expired-banner {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdecea;
  color: #d93025;
  font-size: 13px;
  font-weight: 500;
}

.poll-vote-modal__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 13px;
  color: #65686c;
}

.poll-vote-modal__hint-icon {
  font-size: 16px;
}

.poll-vote-modal__stats {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 10px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1b74e4;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.poll-vote-modal__options {
  margin-top: 16px;
}

.poll-vote-modal__option-row {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
  cursor: pointer;
}

.poll-vote-modal__selector {
  width: 20px;
  height: 20px;
  border: 2px solid #bcc0c4;
  background: #fff;
  flex-shrink: 0;
}

.poll-vote-modal__selector--radio {
  border-radius: 50%;
}

.poll-vote-modal__selector--checkbox {
  border-radius: 4px;
}

.poll-vote-modal__selector--checked {
  border-color: #1b74e4;
  background: #1b74e4;
  box-shadow: inset 0 0 0 3px #fff;
}

.poll-vote-modal__option-bar {
  position: relative;
  min-height: 44px;
  border-radius: 8px;
  background: #f1f2f4;
  overflow: hidden;
}

.poll-vote-modal__option-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: rgba(27, 116, 228, 0.14);
}

.poll-vote-modal__option-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
}

.poll-vote-modal__option-text {
  font-size: 14px;
  color: #26282c;
  line-height: 1.35;
}

.poll-vote-modal__option-count {
  min-width: 24px;
  text-align: right;
  font-size: 14px;
  color: #26282c;
}

.poll-vote-modal__option-count--clickable {
  cursor: pointer;
  color: #1b74e4;
}

.poll-vote-modal__option-avatars {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.poll-vote-modal__avatar {
  margin-left: -6px;
  flex-shrink: 0;
}

.poll-vote-modal__avatar:first-child {
  margin-left: 0;
}

.poll-vote-modal__avatar :deep(.image-template) {
  width: 24px;
  height: 24px;
  border: 2px solid #fff;
}

.poll-vote-modal__avatar-more {
  margin-left: 4px;
  font-size: 12px;
  color: #65686c;
}

.poll-vote-modal__add-option {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: #1b74e4;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 0 12px;
  cursor: pointer;
}

.poll-vote-modal__add-input {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.poll-vote-modal__input {
  flex: 1;
  border: 1px solid #dee0e2;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
}

.poll-vote-modal__add-btn {
  border: 0;
  border-radius: 8px;
  background: #e7f3ff;
  color: #1b74e4;
  padding: 0 14px;
  font-weight: 500;
  cursor: pointer;
}

.poll-vote-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px 16px;
  border-top: 1px solid #e9eaec;
}

.poll-vote-modal__footer-actions {
  display: flex;
  gap: 10px;
}

.poll-vote-modal__footer-actions--full {
  width: 100%;
  justify-content: flex-end;
}

.poll-vote-modal__btn {
  min-width: 96px;
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.poll-vote-modal__btn--ghost {
  border: 0;
  background: #f1f2f4;
  color: #26282c;
}

.poll-vote-modal__btn--primary {
  border: 0;
  background: #1b74e4;
  color: #fff;
}

.poll-vote-modal__btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
